import { ScrollAnimContext } from "@/contexts/ScrollAnimContext";
import { useScroll, motion, useTransform, easeInOut, cubicBezier, useMotionValueEvent } from "motion/react";
import { useRef, useState } from "react";
import PopOut from "../animations/PopOut";
import GradientText from "../GradientText";
import ExperiencesShowcase from "../ExperiencesShowcase/ExperiencesShowcase";
import LettersPullUp from "../animations/LettersPullUp";
import Highlight from "../animations/Highlight";
import { TEAM_COLOURS } from "@/libs/20thCenturyFox";
import { AnimatedSectionsHeading } from "./AnimatedSectionsHeading";

export default function ExperiencesSection() {
  const [displayTitle, setDisplayTitle] = useState(1);
  const container = useRef(null);

  const { scrollYProgress } = useScroll({ target: container, offset: ["start start", "end end"] }); // Progress starts when the section has completely filled the viewport

  // Animate scene transition in
  const sceneStartTransition = useTransform(scrollYProgress, [0, 0.055], [0, 1], { ease: easeInOut });
  const popOutTransition = useTransform(sceneStartTransition, [0.363, 1], [0, 1], { ease: easeInOut });
  const sceneOpacity = useTransform(sceneStartTransition, [0.363, 0.636], [0, 1], { ease: easeInOut });

  // Animate second half of transition in title text
  const titleProgress = useTransform(scrollYProgress, [0.051, 0.28], [0, 1]);
  const width = useTransform(titleProgress, [0, 0.194], ["0%", "100%"], { ease: cubicBezier(0.37, 0, 0.63, 1) });
  const marginLeftRight = useTransform(titleProgress, [0.14, 0.264, 0.74, 1], ["0svw", "0.5svw", "1.5svw", "80svw"], {
    ease: cubicBezier(0.5, 1, 0.89, 1),
  });
  const titleOpacity = useTransform(titleProgress, [0.74, 1], [1, 0], { ease: cubicBezier(0.5, 1, 0.89, 1) });

  // Animate showcase
  const showcaseScene = useTransform(scrollYProgress, [0.06, 1], [0, 1]);
  const systemsTogetherTitle = useTransform(showcaseScene, [0.24, 0.45], [0, 1], { ease: easeInOut });
  const usersDevProgress = useTransform(showcaseScene, [0.4, 0.8], [0, 1]);
  const forUsersTitle = useTransform(usersDevProgress, [0, 0.2], [0, 1], { ease: easeInOut });
  const forDevsTitle = useTransform(usersDevProgress, [0.28, 0.48], [0, 1], { ease: easeInOut });
  const usersHighlight = useTransform(usersDevProgress, [0.12, 0.57], [0, 1], { ease: easeInOut });
  const devsHighlight = useTransform(usersDevProgress, [0.43, 0.83], [0, 1], { ease: easeInOut });
  const usersDevOpacity = useTransform(showcaseScene, [0.65, 0.79], [1, 0], { ease: easeInOut });

  // Set the title to be displayed
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (latest < 0.29) {
      setDisplayTitle(1);
    } else if (latest >= 0.29 && latest < 0.45) {
      setDisplayTitle(2);
    } else if (latest >= 0.45) {
      setDisplayTitle(3);
    }
  });

  return (
    <ScrollAnimContext.Provider value={scrollYProgress}>
      <motion.div
        style={{ height: "1500svh", marginTop: "-150svh", opacity: sceneOpacity }}
        className={"w-full bg-black text-neutral-100"}
        ref={container}
      >
        <div style={{ height: "100svh" }} className='sticky top-0 flex flex-col justify-center items-center w-full'>
          <AnimatedSectionsHeading className='h-full w-full flex justify-center items-center'>
            {/* "I love creating experiences" title */}
            <motion.div
              className='flex items-center justify-center flex-col lg:flex-row'
              hidden={displayTitle !== 1}
              style={{ opacity: titleOpacity }}
            >
              <PopOut className='text-nowrap' style={{ marginRight: marginLeftRight }} scrollProgress={popOutTransition}>
                I love creating
              </PopOut>
              <motion.div style={{ width, marginLeft: marginLeftRight }} className='overflow-hidden 2xl:whitespace-pre text-center'>
                <GradientText text=' experiences' />
              </motion.div>
            </motion.div>
            {/* "bringing systems together" title */}
            <motion.div hidden={displayTitle !== 2}>
              <PopOut className='text-center' scrollProgress={systemsTogetherTitle} fullDuration fadeOut>
                bringing systems together
              </PopOut>
            </motion.div>
            {/* "for users / and for devs" title */}
            <motion.div
              className='min-w-1/2 flex flex-col md:flex-row justify-between whitespace-pre z-10 gap-10 md:gap-16 lg:gap-24'
              hidden={displayTitle !== 3}
              style={{ opacity: usersDevOpacity }}
            >
              <LettersPullUp className='flex md:mb-30' scrollProgress={forUsersTitle}>
                for{" "}
                <Highlight className='px-5' highlightStyles={{ backgroundColor: TEAM_COLOURS.USER }} scrollProgress={usersHighlight}>
                  users
                </Highlight>
              </LettersPullUp>
              <LettersPullUp scrollProgress={forDevsTitle} className='self-end flex ml-10 md:ml-0'>
                and for{" "}
                <Highlight className='px-5' highlightStyles={{ backgroundColor: TEAM_COLOURS.DEV }} scrollProgress={devsHighlight}>
                  devs
                </Highlight>
              </LettersPullUp>
            </motion.div>
          </AnimatedSectionsHeading>
          <div className='-z-10 absolute h-full w-full'>
            <ScrollAnimContext.Provider value={showcaseScene}>
              <ExperiencesShowcase />
            </ScrollAnimContext.Provider>
          </div>
        </div>
      </motion.div>
    </ScrollAnimContext.Provider>
  );
}
